import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MAX_ACTIONS_PER_TICK = 128;
const MAX_EVENT_DEPTH = 8;

function orderedTimeline(manifest) {
  const rows = [];
  for (const track of manifest?.tracks || []) {
    for (const clip of track?.clips || []) {
      rows.push({
        id: `track:${track.id}:${clip.id}`,
        atMs: Number(clip.startMs) || 0,
        actions: Array.isArray(clip.actions) ? clip.actions : [],
      });
    }
  }
  for (const cue of manifest?.cues || []) {
    rows.push({
      id: `cue:${cue.id}`,
      atMs: Number(cue.atMs) || 0,
      actions: Array.isArray(cue.actions) ? cue.actions : [],
    });
  }
  return rows.sort((a, b) => (a.atMs - b.atMs) || a.id.localeCompare(b.id));
}

function triggersByEvent(manifest) {
  const map = new Map();
  for (const trigger of manifest?.triggers || []) {
    const event = String(trigger?.event || "");
    if (!event) continue;
    const list = map.get(event) || [];
    list.push(trigger);
    map.set(event, list);
  }
  return map;
}

const DEFAULT_STAGE = Object.freeze({
  translateX: 0,
  translateY: 0,
  scale: 1,
  rotateDeg: 0,
  opacity: 1,
});

export default function useRealityScriptRuntime({
  script,
  onModeChange,
  onUnsupportedAction,
  onSimulationAction,
  onSimulationReset,
}) {
  const manifest = script?.manifest || null;
  const timeline = useMemo(() => orderedTimeline(manifest), [manifest]);
  const triggerMap = useMemo(() => triggersByEvent(manifest), [manifest]);
  const [status, setStatus] = useState("idle");
  const [timeMs, setTimeMs] = useState(0);
  const [overlay, setOverlay] = useState(null);
  const [stage, setStage] = useState(DEFAULT_STAGE);
  const [variables, setVariables] = useState(() => ({ ...(manifest?.variables || {}) }));

  const frameRef = useRef(null);
  const startedAtRef = useRef(0);
  const elapsedBeforePlayRef = useRef(0);
  const firedTimelineRef = useRef(new Set());
  const firedTriggersRef = useRef(new Set());
  const runtimeTokenRef = useRef(0);

  const cancelFrame = useCallback(() => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const resetState = useCallback(() => {
    cancelFrame();
    onSimulationReset?.();
    runtimeTokenRef.current += 1;
    firedTimelineRef.current = new Set();
    firedTriggersRef.current = new Set();
    elapsedBeforePlayRef.current = 0;
    startedAtRef.current = 0;
    setTimeMs(0);
    setOverlay(null);
    setStage(DEFAULT_STAGE);
    setVariables({ ...(manifest?.variables || {}) });
    setStatus("idle");
  }, [cancelFrame, manifest, onSimulationReset]);

  useEffect(() => {
    resetState();
  }, [script?.id, script?.checksumSha256, resetState]);

  const dispatchActions = useCallback((actions, depth = 0, eventStack = []) => {
    if (!Array.isArray(actions) || depth > MAX_EVENT_DEPTH) return;
    const bounded = actions.slice(0, MAX_ACTIONS_PER_TICK);

    for (const action of bounded) {
      const type = action?.type;
      const payload = action?.payload || {};

      if (type === "player.set_mode") {
        onModeChange?.(payload.mode);
      } else if (type === "overlay.show") {
        setOverlay({
          text: String(payload.text || "").slice(0, 600),
          position: payload.position || "bottom",
          tone: payload.tone || "neutral",
        });
      } else if (type === "overlay.hide") {
        setOverlay(null);
      } else if (type === "stage.set_transform") {
        setStage(current => ({
          ...current,
          translateX: Number(payload.translateX) || 0,
          translateY: Number(payload.translateY) || 0,
          scale: Number(payload.scale) || 1,
          rotateDeg: Number(payload.rotateDeg) || 0,
        }));
      } else if (type === "stage.set_opacity") {
        const value = Number(payload.value);
        if (Number.isFinite(value)) {
          setStage(current => ({ ...current, opacity: Math.max(0, Math.min(1, value)) }));
        }
      } else if (type === "state.set") {
        const key = String(payload.key || "");
        if (key) setVariables(current => ({ ...current, [key]: payload.value }));
      } else if (type === "physics.set_wind" || type === "physics.apply_impulse") {
        if (typeof onSimulationAction === "function") {
          const handled = onSimulationAction(action);
          if (handled === false) onUnsupportedAction?.(action);
        } else {
          onUnsupportedAction?.(action);
        }
      } else if (type === "event.emit") {
        const event = String(payload.event || "");
        if (!event || eventStack.includes(event) || depth >= MAX_EVENT_DEPTH) continue;
        const triggers = triggerMap.get(event) || [];
        for (const trigger of triggers) {
          if (trigger.once && firedTriggersRef.current.has(trigger.id)) continue;
          if (trigger.once) firedTriggersRef.current.add(trigger.id);
          dispatchActions(trigger.actions, depth + 1, [...eventStack, event]);
        }
      } else {
        onUnsupportedAction?.(action);
      }
    }
  }, [onModeChange, onSimulationAction, onUnsupportedAction, triggerMap]);

  const emitEvent = useCallback((event, value = null) => {
    const name = String(event || "");
    if (!name) return;
    const triggers = triggerMap.get(name) || [];
    for (const trigger of triggers) {
      if (trigger.once && firedTriggersRef.current.has(trigger.id)) continue;
      if (trigger.once) firedTriggersRef.current.add(trigger.id);
      dispatchActions(trigger.actions, 1, [name]);
    }
    if (value !== null) {
      setVariables(current => ({ ...current, [`event.${name}`]: value }));
    }
  }, [dispatchActions, triggerMap]);

  const finish = useCallback(() => {
    cancelFrame();
    emitEvent("end");
    const duration = Number(manifest?.durationMs) || 0;
    setTimeMs(duration);

    if (manifest?.loop === true && duration > 0) {
      onSimulationReset?.();
      firedTimelineRef.current = new Set();
      elapsedBeforePlayRef.current = 0;
      setTimeMs(0);
      setStatus("playing");
      runtimeTokenRef.current += 1;
      startedAtRef.current = performance.now();
      return true;
    }

    setStatus("ended");
    return false;
  }, [cancelFrame, emitEvent, manifest, onSimulationReset]);

  const tickRef = useRef(null);
  tickRef.current = (now, token) => {
    if (token !== runtimeTokenRef.current) return;
    const duration = Number(manifest?.durationMs) || 0;
    const elapsed = Math.max(0, Math.min(duration, elapsedBeforePlayRef.current + (now - startedAtRef.current)));
    setTimeMs(elapsed);

    let executed = 0;
    for (const item of timeline) {
      if (item.atMs > elapsed) break;
      if (firedTimelineRef.current.has(item.id)) continue;
      firedTimelineRef.current.add(item.id);
      if (executed >= MAX_ACTIONS_PER_TICK) break;
      const remaining = MAX_ACTIONS_PER_TICK - executed;
      const actions = item.actions.slice(0, remaining);
      executed += actions.length;
      dispatchActions(actions);
    }

    if (elapsed >= duration) {
      const loops = finish();
      if (loops) {
        const nextToken = runtimeTokenRef.current;
        frameRef.current = requestAnimationFrame(next => tickRef.current?.(next, nextToken));
      }
      return;
    }
    frameRef.current = requestAnimationFrame(next => tickRef.current?.(next, token));
  };

  const play = useCallback(() => {
    if (!manifest || !Number(manifest.durationMs)) return false;
    if (status === "playing") return true;

    if (status === "ended") {
      onSimulationReset?.();
      firedTimelineRef.current = new Set();
      firedTriggersRef.current = new Set();
      elapsedBeforePlayRef.current = 0;
      setTimeMs(0);
      setOverlay(null);
      setStage(DEFAULT_STAGE);
      setVariables({ ...(manifest.variables || {}) });
    }

    runtimeTokenRef.current += 1;
    const token = runtimeTokenRef.current;
    startedAtRef.current = performance.now();
    setStatus("playing");
    if (elapsedBeforePlayRef.current === 0) emitEvent("start");
    frameRef.current = requestAnimationFrame(now => tickRef.current?.(now, token));
    return true;
  }, [emitEvent, manifest, onSimulationReset, status]);

  const pause = useCallback(() => {
    if (status !== "playing") return;
    const now = performance.now();
    elapsedBeforePlayRef.current = Math.max(
      0,
      Math.min(Number(manifest?.durationMs) || 0, elapsedBeforePlayRef.current + (now - startedAtRef.current))
    );
    cancelFrame();
    runtimeTokenRef.current += 1;
    setTimeMs(elapsedBeforePlayRef.current);
    setStatus("paused");
  }, [cancelFrame, manifest, status]);

  const stop = useCallback(() => {
    resetState();
  }, [resetState]);

  useEffect(() => () => cancelFrame(), [cancelFrame]);

  useEffect(() => {
    if (manifest?.autoplay === true && status === "idle") play();
  }, [manifest?.autoplay, play, status]);

  return {
    ready: Boolean(manifest),
    status,
    timeMs,
    durationMs: Number(manifest?.durationMs) || 0,
    progress: Number(manifest?.durationMs) > 0 ? timeMs / Number(manifest.durationMs) : 0,
    overlay,
    stage,
    variables,
    play,
    pause,
    stop,
    emitEvent,
  };
}

export { orderedTimeline, triggersByEvent, DEFAULT_STAGE, MAX_ACTIONS_PER_TICK, MAX_EVENT_DEPTH };
